// frontend/admin-portal/src/components/products/BulkPriceUpdateModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Input, Alert, formatMoney } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

export default function BulkPriceUpdateModal({
  open,
  products = [],
  onClose,
  onUpdated,
}) {
  const [mode, setMode] = useState("percentage"); // percentage | absolute
  const [percentage, setPercentage] = useState(10);
  const [absoluteCost, setAbsoluteCost] = useState(0);
  const [recalcPrice, setRecalcPrice] = useState(false);
  const [targetMargin, setTargetMargin] = useState(30);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setMode("percentage");
    setPercentage(10);
    setAbsoluteCost(0);
    setRecalcPrice(false);
    setTargetMargin(30);
  }, [open]);

  const preview = useMemo(() => {
    return products.map((p) => {
      const oldCost = Number(p.costPrice) || 0;
      const oldTotal = Number(p.totalCost) || 0;
      const oldPrice = Number(p.price) || 0;

      let newCost;
      if (mode === "absolute") {
        newCost = Number(absoluteCost) || 0;
      } else {
        newCost = oldCost * (1 + Number(percentage) / 100);
      }
      newCost = Math.max(0, newCost);

      const cbm = Number(p.volumeCbm) || 0;
      const effectiveRate = cbm > 0 ? (oldTotal - oldCost) / cbm : 0;
      const newTotal = newCost + cbm * effectiveRate;

      let newPrice = oldPrice;
      if (recalcPrice) {
        const m = Math.min(Math.max(Number(targetMargin) || 30, 0), 95) / 100;
        newPrice = newTotal / (1 - m);
      }

      return {
        ...p,
        oldCost: +oldCost.toFixed(4),
        newCost: +newCost.toFixed(4),
        oldTotal: +oldTotal.toFixed(2),
        newTotal: +newTotal.toFixed(2),
        oldPrice: +oldPrice.toFixed(2),
        newPrice: +newPrice.toFixed(2),
      };
    });
  }, [products, mode, percentage, absoluteCost, recalcPrice, targetMargin]);

  const handleApply = async () => {
    setApplying(true);
    setError("");
    try {
      const ids = products.map((p) => p.id);
      const payload = {
        ids,
        costMode: mode,
        costValue:
          mode === "absolute" ? Number(absoluteCost) : Number(percentage),
        recalcPrice,
        targetMarginPct: Number(targetMargin),
      };
      await productService.bulkPriceUpdate(payload);
      onUpdated?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Update failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Bulk Update Prices (${products.length} product${products.length === 1 ? "" : "s"})`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApply} loading={applying}>
            Apply to {products.length}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="fh-input-label">Cost change</label>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant={mode === "percentage" ? "primary" : "ghost"}
              onClick={() => setMode("percentage")}
            >
              Percentage
            </Button>
            <Button
              size="sm"
              variant={mode === "absolute" ? "primary" : "ghost"}
              onClick={() => setMode("absolute")}
            >
              Absolute
            </Button>
          </div>
        </div>
        <div className="col-md-6">
          {mode === "percentage" ? (
            <Input
              label="Increase cost by (%)"
              type="number"
              step="0.1"
              value={percentage}
              onChange={(e) => setPercentage(Number(e.target.value) || 0)}
              hint="Enter -10 for a 10% decrease"
            />
          ) : (
            <Input
              label="Set cost to (absolute)"
              type="number"
              step="0.01"
              value={absoluteCost}
              onChange={(e) => setAbsoluteCost(Number(e.target.value) || 0)}
            />
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className="fh-checkbox">
          <input
            type="checkbox"
            checked={recalcPrice}
            onChange={(e) => setRecalcPrice(e.target.checked)}
          />
          <span>Recompute selling price at target margin</span>
        </label>
        {recalcPrice && (
          <div style={{ marginTop: "0.5rem", maxWidth: 300 }}>
            <Input
              label="Target margin (%)"
              type="number"
              step="1"
              min="0"
              max="95"
              value={targetMargin}
              onChange={(e) => setTargetMargin(Number(e.target.value) || 0)}
            />
          </div>
        )}
      </div>

      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid var(--fh-border, #eef0f3)",
          borderRadius: 8,
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
        >
          <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
            <tr>
              <th style={{ padding: "0.5rem", textAlign: "left" }}>Product</th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>
                Old cost
              </th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>
                New cost
              </th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>
                Old landed
              </th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>
                New landed
              </th>
              {recalcPrice && (
                <>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>
                    Old price
                  </th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>
                    New price
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {preview.map((p) => {
              const costDelta = p.newCost - p.oldCost;
              const priceDelta = p.newPrice - p.oldPrice;
              return (
                <tr
                  key={p.id}
                  style={{ borderTop: "1px solid var(--fh-border, #eef0f3)" }}
                >
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <div
                      style={{
                        fontWeight: 600,
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={p.name}
                    >
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {p.sku}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "0.4rem 0.5rem",
                      textAlign: "right",
                      fontFamily: "monospace",
                    }}
                  >
                    ${p.oldCost.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "0.4rem 0.5rem",
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: costDelta >= 0 ? "#198754" : "#dc3545",
                    }}
                  >
                    ${p.newCost.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "0.4rem 0.5rem",
                      textAlign: "right",
                      fontFamily: "monospace",
                    }}
                  >
                    ${p.oldTotal.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "0.4rem 0.5rem",
                      textAlign: "right",
                      fontFamily: "monospace",
                    }}
                  >
                    ${p.newTotal.toFixed(2)}
                  </td>
                  {recalcPrice && (
                    <>
                      <td
                        style={{
                          padding: "0.4rem 0.5rem",
                          textAlign: "right",
                          fontFamily: "monospace",
                        }}
                      >
                        ${p.oldPrice.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "0.4rem 0.5rem",
                          textAlign: "right",
                          fontFamily: "monospace",
                          color: priceDelta >= 0 ? "#198754" : "#dc3545",
                        }}
                      >
                        ${p.newPrice.toFixed(2)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Alert variant="info" className="mt-3">
        Landed cost is recomputed from the current shipment rate stored on each
        product. Only costPrice, totalCost, and (optionally) price are changed.
        Stock, attributes, images — untouched.
      </Alert>
    </Modal>
  );
}
